/**
 * Child (in-iframe) side of the embed protocol. The app running inside the cross-origin iframe posts
 * `ready` once and `resize` on every layout change so the loader can size the frame — and NOTHING
 * else crosses the boundary (spec R6). It posts ONLY to the embedding origin captured at handshake,
 * never to `*`.
 *
 * Handshake: the trusted parent origin is the origin of `document.referrer` (the embedding page) —
 * captured once at load. If the host stripped the referrer, we cannot know a safe target, so we stay
 * SILENT rather than post to `*` (the loader's ready-timeout then degrades to its anchor). When not
 * framed at all (`window.parent === window`, i.e. the standalone page), there is no bridge.
 */
import { isConcreteOrigin, makeMessage, postMessageTo, type WidgetMessageType } from "./protocol";

/** The origin to post to, from the embedding page's referrer — or null when unknowable/unsafe. */
export function resolveParentOrigin(referrer: string | null | undefined): string | null {
  if (!referrer) return null;
  try {
    const origin = new URL(referrer).origin;
    return isConcreteOrigin(origin) ? origin : null;
  } catch {
    return null;
  }
}

export interface ChildBridge {
  /** Announce the shell is mounted (clears the loader's fallback timer). */
  ready(): void;
  /** Report the current content height so the loader resizes the iframe. */
  resize(height: number): void;
  /** Ask a floating overlay to open / close (diner-initiated, e.g. a close button). */
  open(): void;
  close(): void;
  /** Observe an element and emit `resize` on every size change (+ an initial `ready`). */
  observe(element: Element): void;
  stop(): void;
}

/** A no-op bridge for the standalone (non-framed) page — same surface, zero postMessage traffic. */
const NO_OP: ChildBridge = {
  ready() {},
  resize() {},
  open() {},
  close() {},
  observe() {},
  stop() {},
};

interface BridgeDeps {
  win: Pick<Window, "parent"> & { parent: Window };
  parentOrigin: string | null;
}

/** Best-effort content height for a resize message (max of layout + scroll height, rounded up). */
function measure(element: Element): number {
  return Math.ceil(Math.max((element as HTMLElement).offsetHeight ?? 0, element.scrollHeight ?? 0));
}

/**
 * Build the child bridge. Returns a no-op when the page is not framed or the parent origin is
 * unknowable — so callers never special-case; they just `observe(rootEl)` and let the bridge decide.
 */
export function createChildBridge(deps: BridgeDeps): ChildBridge {
  const framed = deps.win.parent !== (deps.win as unknown as Window);
  if (!framed || !deps.parentOrigin) return NO_OP;

  const target = deps.win.parent;
  const origin = deps.parentOrigin;
  const post = (type: WidgetMessageType, height?: number) =>
    postMessageTo(target, origin, makeMessage(type, height));

  let observer: ResizeObserver | null = null;
  let frame = 0;

  return {
    ready: () => void post("ready"),
    resize: (height: number) => void post("resize", height),
    open: () => void post("open"),
    close: () => void post("close"),
    observe(element: Element) {
      post("ready");
      post("resize", measure(element));
      if (typeof ResizeObserver === "undefined") return;
      observer = new ResizeObserver(() => {
        if (frame) cancelAnimationFrame(frame);
        // Coalesce bursts to one message per frame — resize storms must not flood the host.
        const raf =
          typeof requestAnimationFrame === "function" ? requestAnimationFrame : setTimeout;
        frame = raf(() => post("resize", measure(element))) as unknown as number;
      });
      observer.observe(element);
    },
    stop() {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      observer = null;
    },
  };
}
